import { Button, Tooltip } from "@xola/ui-kit";
import { uniqueId } from "lodash";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Experience, Guest, Membership } from "types";
import shallow from "zustand/shallow";
import { isOrganizerSelected } from "../../../models/Guest";
import { useCreatePurchaseStore } from "../../../stores/createPurchaseStore";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ContentLayout } from "../../Layout/ContentLayout";
import { CheckoutButton } from "../CheckoutButton";
import { CreatePurchaseFooter } from "../CreatePurchaseFooter";
import { CreatePurchaseSidebar } from "../CreatePurchaseSidebar";
import { getPurchaseItemsHavingId, handleGuestListChange } from "../helpers/PurchaseCreateHelpers";
import { getMembershipValidations } from "../hooks/getMembershipValidations";
import { getCreatePurchasePackageFields } from "../hooks/useCreatePurchasePackageFields";
import { CreatePurchaseMembershipFields } from "@/components/PurchaseCreate/Membership/CreatePurchaseMembershipFields";

interface CreatePurchaseMembershipScreenProps {
    membership: Membership;
    onSubmit: () => void;
}

export const CreatePurchaseMembershipScreen = ({ membership, onSubmit }: CreatePurchaseMembershipScreenProps) => {
    const history = useHistory();
    const shouldDisableFooter = useCreatePurchaseStore((state) => !state.purchaseId);

    const { showNextButton } = getCreatePurchasePackageFields();
    const { getDisabledReasons } = getMembershipValidations();

    const purchase = useCreatePurchaseStore((state) => state.purchase);
    const [setPurchase, resetState] = useCreatePurchaseStore(
        (state) => [state.actions.setPurchase, state.actions.resetState],
        shallow,
    );
    const setActiveItemIndex = useCreatePurchaseStore((state) => state.actions.setActiveItemIndex);
    const getActiveExperience = useCreatePurchaseStore((state) => state.getActiveProduct);
    const item = useCreatePurchaseStore((state) => state.getActiveItem());

    const disabledReasons = getDisabledReasons();

    const handleCancel = () => {
        resetState();
        history.push("/cart/init");
    };

    const handleAddNewProductClick = () => {
        setPurchase((draft) => getPurchaseItemsHavingId(draft));

        history.push("/cart/init", { isAddingItem: true });
    };

    const handleNext = () => {
        const activeExperience: Experience = getActiveExperience();
        const index = purchase.items.findIndex((item) => item.product.id === activeExperience.id);
        setActiveItemIndex(index + 1);
    };

    const handleCheckout = (): any => {
        if (item.guests?.length === 1 && !isOrganizerSelected(item.guests)) {
            setMainGuest(item.guests);
        }

        onSubmit();
    };

    const popoverParentId = uniqueId("popoverParentId");

    useEffect(() => {
        return () => {
            if (history.action === "POP" && purchase.items.length === 1) {
                /** User has clicked on a product in create purchase flow and clicked browser back button, so we need to reset the CreatePurchase state to initial */
                resetState();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="grid h-full grid-cols-12" id={popoverParentId}>
            <ContentLayout isWide classNames={{ root: "relative col-span-6 h-full 2xl:col-span-7" }}>
                <h3 className="mb-6">Membership Details</h3>

                <CreatePurchaseMembershipFields membership={membership} />

                <CreatePurchaseFooter secondaryText="Cancel" onCancel={handleCancel}>
                    {disabledReasons.length > 0 ? (
                        <Tooltip
                            content={disabledReasons.map((message, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <div key={index}>{message}</div>
                            ))}
                        >
                            <CheckoutButton disabled label={showNextButton ? "Next" : "Checkout"} />
                        </Tooltip>
                    ) : showNextButton ? (
                        <Button disabled={shouldDisableFooter} onClick={handleNext}>
                            Next
                        </Button>
                    ) : (
                        <CheckoutButton disabled={shouldDisableFooter} onClick={handleCheckout} />
                    )}

                    <SecondaryButton disabled={shouldDisableFooter} onClick={handleAddNewProductClick}>
                        Add Another Product
                    </SecondaryButton>
                </CreatePurchaseFooter>
            </ContentLayout>

            <div className="col-span-6 shrink-0 space-y-6 bg-gray-lighter 2xl:col-span-5">
                <CreatePurchaseSidebar />
            </div>
        </div>
    );
};

const setMainGuest = (guests: Guest[]) => {
    handleGuestListChange(
        guests.map((guest) => {
            return { ...guest, primary: true };
        }),
    );
};
